// One address line of the Send PO window — To, CC or BCC — like commonsku
// (Stephanie 2026-10-14, Jake approved). Chips for who's on it; one box to
// type an email or pick one of the supplier's contacts (the browser's own
// suggestion list), Enter or "Add" to put it on.
import { useId, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

import {
  resolveAddressInput,
  type AddressBookEntry,
  type AddressEntry,
} from "@merchd/shared";

export { resolveAddressInput, withTypedAddress, type AddressBookEntry, type AddressEntry } from "@merchd/shared";

export function AddressField(props: {
  label: string;
  entries: AddressEntry[];
  onChange: (next: AddressEntry[]) => void;
  book: readonly AddressBookEntry[];
  /** Every address already on the email (To + CC + BCC), lowercased. */
  taken: ReadonlySet<string>;
  disabled?: boolean;
  placeholder?: string;
  /** The typed-but-not-Added text, when the window needs it on Send. */
  draft?: string;
  onDraftChange?: (v: string) => void;
  /** An error the window sets (e.g. the box isn't an address on Send). */
  externalError?: string | null;
}) {
  const { label, entries, onChange, book, taken, disabled, placeholder } = props;
  const [ownValue, setOwnValue] = useState("");
  const value = props.draft ?? ownValue;
  const setValue = (v: string): void => {
    if (props.onDraftChange) props.onDraftChange(v);
    else setOwnValue(v);
  };
  const [ownError, setError] = useState<string | null>(null);
  const error = ownError ?? props.externalError ?? null;
  const listId = useId();
  const add = (): void => {
    const r = resolveAddressInput(value, book, taken);
    if ("error" in r) {
      setError(r.error);
      return;
    }
    onChange([...entries, r.add]);
    setValue("");
    setError(null);
  };
  const offered = book.filter((b) => !taken.has(b.email.toLowerCase()));
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </label>
      {entries.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {entries.map((e, i) => (
            <li
              key={`${e.email}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-peach-300 bg-peach-100 px-2.5 py-1 text-xs text-brown-500"
            >
              <span className="font-medium">{e.name ? `${e.name} · ${e.email}` : e.email}</span>
              <button
                type="button"
                onClick={() => onChange(entries.filter((_, idx) => idx !== i))}
                disabled={disabled}
                className="text-brown-300 hover:text-rust-500 disabled:opacity-50"
                aria-label={`Remove ${e.email} from ${label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="email"
          list={listId}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder ?? (offered.length > 0 ? "Type an email or pick a contact" : "Type an email")}
          aria-label={`Add to ${label}`}
          className="h-9 flex-1 text-sm"
          disabled={disabled}
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={disabled || value.trim() === ""}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>
      <datalist id={listId}>
        {offered.map((b) => (
          <option key={b.email} value={b.email}>
            {[b.name, b.role].filter(Boolean).join(" · ")}
          </option>
        ))}
      </datalist>
      {error && <p className="mt-1 text-xs text-rust-600">{error}</p>}
    </div>
  );
}
